// frontend/src/utils/referral.js
//
// Remembering who sent a visitor here, from the moment they arrive to the moment
// they create an account.
//
// The gap between those two is the whole problem. Someone scans a QR code on a
// salon's mirror, browses for ten minutes, closes the tab, comes back that
// evening and signs up — and by then the ?ref= that carried the salon's code is
// long gone from the URL. So it is parked in localStorage and read back at
// registration, which is the one moment the server can bind it permanently.
//
// ── Why the window is finite ────────────────────────────────────────────────
//
// A code kept for ever would credit a partner for a customer who signed up eight
// months later for entirely unrelated reasons — and the customer, not the
// partner, is the one whose device is holding the claim. Thirty days is long
// enough for "I'll do it at the weekend" and short enough that the referral is
// still plausibly the reason.

import api from './api';

const KEY = 'glamgo_ref';
const WINDOW_DAYS = 30;

// localStorage throws rather than returning null in Safari private browsing, and
// a referral is not worth a white screen — every access here is guarded.
const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* private browsing, or storage full. The referral is lost; nothing else is. */
  }
};

export const clearReferral = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
};

/** The stored code, or null if there is none or it has aged out. */
export function getReferral() {
  const stored = read();
  if (!stored || !stored.code) return null;
  const age = Date.now() - (stored.at || 0);
  if (age > WINDOW_DAYS * 86400000) {
    clearReferral();
    return null;
  }
  return stored.code;
}

/**
 * Record a code the visitor arrived with.
 *
 * FIRST touch wins, and it wins here as well as on the server. The server's
 * affiliate_referrals table is the real guarantee — its primary key is the user
 * — but a second partner's code overwriting the first in localStorage would mean
 * the wrong code is the one that ever reaches the server, and the first partner
 * loses a customer they genuinely sent without anything ever recording that they
 * did.
 */
export function rememberReferral(code, { path } = {}) {
  const clean = String(code || '').trim().toUpperCase().slice(0, 50);
  if (!clean) return null;

  const existing = getReferral();
  if (existing) return existing;

  write({ code: clean, at: Date.now() });

  // Fire-and-forget. The click counter is the partner's funnel report, and a
  // visitor must never wait on it or see it fail — a rejected or offline call
  // simply means one uncounted scan.
  api.post('/affiliates/track', { code: clean, path: path || window.location.pathname })
    .catch(() => { /* a click that was not counted is not an error the visitor owns */ });

  return clean;
}

/**
 * Read ?ref= off the current URL, remember it, and strip it.
 *
 * Stripped with replaceState rather than a navigation, for two reasons: the
 * visitor should not get a history entry they can go "back" into, and a ?ref=
 * left in the address bar is what gets copied into a WhatsApp message and
 * credits a salon for a customer who never went near it.
 */
export function captureReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (!code) return getReferral();

    const remembered = rememberReferral(code);

    params.delete('ref');
    const query = params.toString();
    window.history.replaceState(
      {}, '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    );
    return remembered;
  } catch {
    return null;
  }
}
